import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useModal } from "../../context/ModalContext";
import { useToast } from "../../context/ToastContext.jsx";
import { UserContext } from "../../context/UserContext.jsx";
import { api } from "../../api";
import { configureIugu } from "../../services/IuguService";
import Button from "../Button.jsx";
import CardPaymentProcessingNotice from "../CardPaymentProcessingNotice.jsx";
import FormCheck from "../FormCheck.jsx";
import FormInput from "../FormInput.jsx";
import FormRadio from "../FormRadio.jsx";
import FormSelect from "../FormSelect.jsx";
import Spinner from "../Spinner.jsx";
import CountdownTimer from "../utils/CountdownTimer";
import fullLogo from "../../assets/images/svg/unicornio-brown.svg";
import "../../assets/styles/Dashboard.css";
import "../../assets/styles/components/modals/ModalPendingPayment.css";

const ModalPendingPayment = ({ data }) => {
  const { closeModal, setModalLocked } = useModal();
  const { toast } = useToast();
  const userCtx = useContext(UserContext) || {};
  const {
    paymentMethods: contextPaymentMethods,
    setPaymentMethodsFromApi,
    reloadPaymentMethods,
  } = userCtx;

  const rawInvoiceId = data?.invoiceId || null;
  const invoiceUrl = data?.invoiceUrl || null;

  // Se invoiceId não foi passado diretamente, tenta extrair da invoiceUrl (ex.: faturas.iugu.com/{id})
  const invoiceId = React.useMemo(() => {
    if (rawInvoiceId) return String(rawInvoiceId);
    if (invoiceUrl) {
      const match = String(invoiceUrl).match(/faturas\.iugu\.com\/([a-zA-Z0-9_-]+)/);
      if (match && match[1]) return match[1];
    }
    return null;
  }, [rawInvoiceId, invoiceUrl]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [copiedPix, setCopiedPix] = useState(false);
  const [copiedSlip, setCopiedSlip] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [targetDate] = useState(() => new Date(Date.now() + 15 * 60 * 1000));

  // Estados de pagamento (mesmo padrão do ModalAddonPayment)
  const [fetchedCards, setFetchedCards] = useState(null);
  const savedCards =
    fetchedCards !== null ? fetchedCards : contextPaymentMethods || [];

  const [selectedMethod, setSelectedMethod] = useState("credit_card");
  const [cardTab, setCardTab] = useState(
    savedCards.length > 0 ? "saved" : "new",
  );
  const [selectedCardId, setSelectedCardId] = useState(
    savedCards[0]?.payment_method_id || null,
  );
  const [cardData, setCardData] = useState({
    card_name: "",
    card_number: "",
    card_expiration: "",
    card_cvv: "",
  });
  const [cardType, setCardType] = useState("");
  const [cardDigits, setCardDigits] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const [cardLabel, setCardLabel] = useState("");
  const [installments, setInstallments] = useState(1);
  const [payingCard, setPayingCard] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Bloqueia fechamento do modal ao clicar fora ou no X enquanto processa cobrança
  useEffect(() => {
    setModalLocked?.(payingCard);
    return () => {
      setModalLocked?.(false);
    };
  }, [payingCard, setModalLocked]);

  useEffect(() => {
    if (!payingCard) return;

    const modalEl = document.getElementById("bootstrapModal");

    const preventClose = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (modalEl) {
      modalEl.addEventListener("hide.bs.modal", preventClose, true);
    }
    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      if (modalEl) {
        modalEl.removeEventListener("hide.bs.modal", preventClose, true);
      }
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [payingCard]);

  const tokenGenerating = useRef(false);
  const paymentErrorRef = useRef(null);

  const isPaidRef = useRef(false);
  isPaidRef.current = isPaid;

  const onSuccessRef = useRef(data?.onSuccess);
  onSuccessRef.current = data?.onSuccess;
  const successHandledRef = useRef(false);

  const handleSuccess = useCallback(() => {
    if (isPaidRef.current) return;
    setIsPaid(true);
    toast.success("Pagamento confirmado com sucesso!");
    if (!successHandledRef.current && typeof onSuccessRef.current === "function") {
      successHandledRef.current = true;
      try {
        onSuccessRef.current();
      } catch (e) {
        console.error("[ModalPendingPayment] Erro no callback onSuccess:", e);
      }
    }
    setTimeout(() => {
      closeModal();
    }, 2500);
  }, [toast, closeModal]);

  const handleSuccessRef = useRef(handleSuccess);
  handleSuccessRef.current = handleSuccess;

  const applySavedCards = useCallback(
    (cardsList) => {
      const validCards = Array.isArray(cardsList) ? cardsList : [];
      setFetchedCards(validCards);
      setPaymentMethodsFromApi?.(validCards);
      if (validCards.length > 0) {
        setCardTab("saved");
        const defaultCard =
          validCards.find(
            (c) => Number(c?.is_default) === 1 || c?.default === true,
          ) || validCards[0];
        setSelectedCardId(defaultCard?.payment_method_id || null);
      } else {
        setCardTab("new");
        setSelectedCardId(null);
      }
    },
    [setPaymentMethodsFromApi],
  );

  // Carrega script Iugu se ainda não estiver na página
  useEffect(() => {
    if (
      typeof document !== "undefined" &&
      !document.querySelector('script[src="https://js.iugu.com/v2"]')
    ) {
      const script = document.createElement("script");
      script.src = "https://js.iugu.com/v2";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Carrega os detalhes da fatura + cartões salvos do usuário
  useEffect(() => {
    let isMounted = true;

    if (!invoiceId) {
      setLoading(false);
      setError("Identificador da fatura não informado.");
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/invoices/${invoiceId}/payment-details`);
        if (!isMounted) return;

        const dataDetails = res?.data?.data || null;
        setDetails(dataDetails);

        if (["paid", "externally_paid"].includes(dataDetails?.status)) {
          handleSuccessRef.current?.();
          return;
        }

        if (["canceled", "expired"].includes(dataDetails?.status)) {
          toast.error("Esta fatura foi cancelada ou expirou.");
        }

        if (dataDetails?.installments) {
          setInstallments(Math.max(1, Number(dataDetails.installments) || 1));
        }

        const hasPixDetails = Boolean(
          dataDetails?.pix?.qrcode || dataDetails?.pix?.qrcode_text,
        );
        const hasSlipDetails = Boolean(
          dataDetails?.bank_slip?.digitable_line || dataDetails?.bank_slip?.url,
        );
        const isCreditCardOrder =
          String(dataDetails?.payment_method || "").toLowerCase() === "credit_card";
        const supportsCard =
          dataDetails?.accepts_credit_card === true || isCreditCardOrder;

        if (isCreditCardOrder) {
          setSelectedMethod("credit_card");
        } else if (hasPixDetails) {
          setSelectedMethod("pix");
        } else if (hasSlipDetails) {
          setSelectedMethod("bank_slip");
        } else if (supportsCard) {
          setSelectedMethod("credit_card");
        } else {
          setSelectedMethod("external");
        }

        if (
          Array.isArray(dataDetails?.payment_methods) &&
          dataDetails.payment_methods.length > 0
        ) {
          applySavedCards(dataDetails.payment_methods);
        } else if (supportsCard) {
          try {
            const pmRes = await api.get("/users/payment-methods");
            if (isMounted) {
              const pmList = pmRes?.data?.data || pmRes?.data || [];
              if (Array.isArray(pmList)) {
                applySavedCards(pmList);
              }
            }
          } catch {
            // Fallback silencioso caso o endpoint falhe em testes unitários
          }
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("[ModalPendingPayment] Erro ao buscar detalhes:", err);
        setError("Não foi possível carregar os detalhes do pagamento.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [invoiceId, applySavedCards]);

  // Sincroniza cartões salvos se o UserContext atualizar depois
  useEffect(() => {
    if (fetchedCards === null && savedCards.length > 0) {
      setCardTab("saved");
      if (!selectedCardId) {
        const defaultCard =
          savedCards.find(
            (c) => Number(c?.is_default) === 1 || c?.default === true,
          ) || savedCards[0];
        setSelectedCardId(defaultCard?.payment_method_id || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCards.length, fetchedCards]);

  // Polling para checar status do pagamento a cada 4 segundos
  useEffect(() => {
    if (!invoiceId || isPaid || payingCard) return;

    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/webhooks/checkInvoiceStatus?invoice_id=${invoiceId}`);
        const status = res?.data?.data?.status ?? res?.data?.status;

        if (["paid", "externally_paid"].includes(status)) {
          clearInterval(interval);
          handleSuccessRef.current?.();
        } else if (["canceled", "expired"].includes(status)) {
          clearInterval(interval);
          toast.error("Esta fatura foi cancelada ou expirou.");
        }
      } catch (err) {
        console.error("[ModalPendingPayment] Erro no polling de status:", err);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [invoiceId, isPaid, payingCard]);

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    setCardData((prev) => ({ ...prev, [name]: value }));
  };

  const getPixQrSrc = (raw) => {
    if (!raw) return "";
    if (/^data:image\//i.test(raw) || /^https?:\/\//i.test(raw)) {
      return raw;
    }
    return `data:image/png;base64,${raw}`;
  };

  const handleCopyText = async (text, type = "pix") => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const temp = document.createElement("textarea");
        temp.value = text;
        temp.style.position = "fixed";
        temp.style.opacity = "0";
        document.body.appendChild(temp);
        temp.select();
        document.execCommand("copy");
        document.body.removeChild(temp);
      }

      if (type === "pix") {
        setCopiedPix(true);
        setTimeout(() => setCopiedPix(false), 3000);
      } else {
        setCopiedSlip(true);
        setTimeout(() => setCopiedSlip(false), 3000);
      }
    } catch (err) {
      console.error("[ModalPendingPayment] Erro ao copiar:", err);
    }
  };

  const gerarTokenIugu = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (tokenGenerating.current) {
          return reject(new Error("Token já em geração"));
        }
        const form = document.getElementById("pending-invoice-card-form");
        if (!form) {
          return reject(new Error("Formulário de cartão não encontrado"));
        }
        // eslint-disable-next-line no-undef
        if (typeof Iugu === "undefined") {
          return reject(
            new Error(
              "Biblioteca de pagamento não carregada. Aguarde e tente novamente.",
            ),
          );
        }
        tokenGenerating.current = true;
        // eslint-disable-next-line no-undef
        configureIugu(Iugu)
          .then(() => {
            // eslint-disable-next-line no-undef
            Iugu.createPaymentToken(form, (response) => {
              tokenGenerating.current = false;
              if (response.errors) {
                return reject(new Error(JSON.stringify(response.errors)));
              }
              if (!response.id) {
                return reject(new Error("Resposta sem ID de token"));
              }
              resolve(response.id);
            });
          })
          .catch((err) => {
            tokenGenerating.current = false;
            reject(err);
          });
      }),
    [],
  );

  const validateNewCard = () => {
    const { card_name, card_number, card_expiration, card_cvv } = cardData;
    if (!card_name || !card_number || !card_expiration || !card_cvv) {
      const msg = "Preencha todos os campos do cartão.";
      setPaymentError({ title: "Dados incompletos", message: msg });
      toast.warn?.(msg) || toast.error(msg);
      return false;
    }
    if (String(card_cvv).trim().length < 3) {
      const msg = "CVV deve ter no mínimo 3 dígitos.";
      setPaymentError({ title: "Dados inválidos", message: msg });
      toast.warn?.(msg) || toast.error(msg);
      return false;
    }
    return true;
  };

  const handlePayWithCard = async () => {
    if (!invoiceId || payingCard) return;
    setPaymentError(null);

    const usingSavedCard = cardTab === "saved" && savedCards.length > 0;
    if (usingSavedCard && !selectedCardId) {
      const msg = "Selecione um cartão salvo para continuar.";
      setPaymentError({ title: "Cartão não selecionado", message: msg });
      toast.error(msg);
      return;
    }

    let paymentToken = null;
    if (!usingSavedCard) {
      if (!validateNewCard()) return;
      try {
        setPayingCard(true);
        paymentToken = await gerarTokenIugu();
      } catch (err) {
        const msg =
          "Erro ao processar cartão: " + (err?.message || String(err));
        setPaymentError({
          title: "Dados do cartão inválidos",
          message: msg,
        });
        toast.error(msg);
        setPayingCard(false);
        return;
      }
    }

    setPayingCard(true);
    try {
      const payload = {
        payment_method: "credit_card",
        payment_method_id: usingSavedCard ? selectedCardId : null,
        payment_token: paymentToken,
        installments: Number(installments) || 1,
        save_card: !usingSavedCard ? Boolean(saveCard) : false,
        card_label: !usingSavedCard && saveCard ? cardLabel || null : null,
      };

      const res = await api.post(`/invoices/${invoiceId}/pay`, payload);
      const resData = res?.data?.data || res?.data || {};
      const chargeStatus = String(
        resData?.charge?.status || resData?.status || "",
      ).toLowerCase();

      if (
        res?.data?.status === "success" ||
        resData?.status === 1 ||
        ["paid", "captured", "authorized"].includes(chargeStatus)
      ) {
        if (!usingSavedCard && saveCard && reloadPaymentMethods) {
          try {
            await reloadPaymentMethods();
          } catch {}
        }
        handleSuccess();
        return;
      }

      const fallbackMsg =
        resData?.charge?.user_message ||
        resData?.charge?.message ||
        res?.data?.message ||
        "Não foi possível autorizar o pagamento com este cartão.";
      setPaymentError({
        title: "Transação negada",
        message: fallbackMsg,
      });
      toast.error(fallbackMsg);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao processar pagamento.";
      setPaymentError({
        title: "Transação negada",
        message: msg,
      });
      toast.error(msg);
      setTimeout(() => {
        paymentErrorRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    } finally {
      setPayingCard(false);
    }
  };

  const effectiveInvoiceUrl = details?.invoice_url || invoiceUrl;
  const pixData = details?.pix || null;
  const bankSlipData = details?.bank_slip || null;
  const hasPix = Boolean(pixData?.qrcode || pixData?.qrcode_text);
  const hasBankSlip = Boolean(bankSlipData?.digitable_line || bankSlipData?.url);
  const canPayWithCard = Boolean(
    details?.accepts_credit_card === true ||
      String(details?.payment_method || "").toLowerCase() === "credit_card",
  );
  const maxInstallments = Math.max(1, Number(details?.max_installments) || 1);
  const numericTotal = Number(details?.total) || 0;

  const fmt = (v) =>
    Number(v || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formattedTotal = details?.total_formatted
    ? details.total_formatted
    : numericTotal > 0
      ? `R$ ${fmt(numericTotal)}`
      : null;

  const getInstallmentOptions = () => {
    const options = [];
    for (let i = 1; i <= maxInstallments; i++) {
      if (numericTotal > 0) {
        const val = (numericTotal / i).toFixed(2).replace(".", ",");
        options.push({
          value: String(i),
          label: `${i}x de R$ ${val}${i === 1 ? " (à vista)" : " sem juros"}`,
        });
      } else {
        options.push({
          value: String(i),
          label: `${i}x${i === 1 ? " (à vista)" : " sem juros"}`,
        });
      }
    }
    return options;
  };

  const planImage = React.useMemo(() => {
    if (details?.plan_image) return details.plan_image;
    const name = String(details?.plan_name || "").toLowerCase();
    if (name.includes("premium")) return "kitNewPremium.png";
    if (name.includes("avanc") || name.includes("avanç")) return "kitNewAvancado.png";
    return "kitNewEssencial.png";
  }, [details]);

  const planTitle = React.useMemo(() => {
    const raw = String(details?.plan_name || "").trim();
    if (!raw) return "ASSINATURA PURÍSSIMA";
    if (/^plano\s+/i.test(raw)) return raw.toUpperCase();
    return `PLANO ${raw.toUpperCase()}`;
  }, [details]);

  const subscriptionDuration = React.useMemo(() => {
    if (details?.subscription_duration) return details.subscription_duration;
    const name = String(details?.plan_name || "").toLowerCase();
    const interval = Number(details?.subscription_interval || 0);
    if (interval === 12 || name.includes("anual")) return "Assinatura Anual (12 meses)";
    if (interval === 3 || name.includes("trimestral")) return "Assinatura Trimestral (3 meses)";
    if (interval === 1 || name.includes("mensal")) return "Assinatura Mensal (1 mês)";
    if (details?.subscription_id) return "Assinatura Puríssima";
    return "Pedido Único";
  }, [details]);

  const isAvulso = React.useMemo(() => {
    return Boolean(
      details?.is_avulso === true ||
      (!details?.subscription_id && (subscriptionDuration === "Pedido Único" || String(details?.plan_name || "").toLowerCase() === "pedido único"))
    );
  }, [details, subscriptionDuration]);

  const modalHeaderTitle = React.useMemo(() => {
    if (selectedMethod === "pix" && hasPix && !canPayWithCard) {
      return "Pagamento via PIX";
    }
    if (hasBankSlip && !canPayWithCard) {
      return "Boleto Bancário";
    }
    if (isAvulso) {
      return "Pagamento do pedido";
    }
    return "Pagamento da assinatura";
  }, [selectedMethod, hasPix, canPayWithCard, hasBankSlip, isAvulso]);

  const modalSubtitle = React.useMemo(() => {
    if (isAvulso) {
      return "Conclua o pagamento para confirmar seu pedido e iniciar a produção.";
    }
    return "Conclua o pagamento para ativar sua assinatura e iniciar a produção.";
  }, [isAvulso]);

  const successMessage = isAvulso
    ? "Seu pedido foi confirmado e enviado para produção com sucesso."
    : "Sua assinatura foi ativada com sucesso.";

  const chargeNotice = React.useMemo(() => {
    if (!formattedTotal) return null;

    const maxInst = Math.max(
      1,
      Number(details?.max_installments || details?.installments || 1)
    );
    const acceptsCard = canPayWithCard;
    const acceptsPix = Boolean(hasPix || details?.accepts_pix);

    let paymentMethodText = "";
    if (acceptsCard && acceptsPix) {
      paymentMethodText =
        maxInst > 1
          ? `no seu cartão pagos em até ${maxInst}x ou à vista no Pix`
          : `no seu cartão ou à vista no Pix`;
    } else if (acceptsPix && !acceptsCard) {
      paymentMethodText = `à vista no Pix`;
    } else {
      paymentMethodText =
        maxInst > 1
          ? `no seu cartão pagos em até ${maxInst}x`
          : `no seu cartão à vista`;
    }

    return (
      <>
        Será cobrado o valor de <strong>{formattedTotal}</strong> {paymentMethodText}
      </>
    );
  }, [formattedTotal, details, canPayWithCard, hasPix]);

  return (
    <div
      className="ModalConfirmation modal-pending-payment"
      style={{
        backgroundColor: "#E6DDCE",
        borderRadius: "24px",
        position: "relative",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12)",
        maxWidth: "520px",
        margin: "0 auto",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Pagamento da fatura"
    >
      <button
        type="button"
        className="btn-close position-absolute"
        style={{
          top: "20px",
          right: "20px",
          zIndex: 10,
          opacity: payingCard ? 0.3 : 1,
          pointerEvents: payingCard ? "none" : "auto",
          cursor: payingCard ? "not-allowed" : "pointer",
        }}
        onClick={(e) => {
          if (payingCard) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          closeModal();
        }}
        disabled={payingCard}
        aria-label="Close"
      />

      <div className="d-flex justify-content-center mb-3 mb-md-4">
        <img
          src={fullLogo}
          alt="Puríssima"
          style={{ maxHeight: "80px", width: "auto" }}
        />
      </div>

      <h3
        className="mb-2 text-uppercase text-center"
        style={{
          color: "#3D2C06",
          fontWeight: 700,
          letterSpacing: "0.5px",
          fontSize: "1.25rem",
        }}
      >
        {modalHeaderTitle}
      </h3>
      <p className="text-center mb-4" style={{ color: "#7a6a4f", fontSize: "14px" }}>
        {modalSubtitle}
      </p>

      {isPaid ? (
        <div className="payment-success-content text-center py-4 px-4">
          <div className="success-icon mb-3">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#446d41"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" stroke="#446d41" strokeWidth="2" />
              <path d="M8 12l2.5 2.5L16 9" />
            </svg>
          </div>
          <h3 className="payment-title text-success mb-2">
            <strong>Pagamento Confirmado!</strong>
          </h3>
          <p className="text-muted mb-0">
            {successMessage}
          </p>
          <p className="small text-muted mt-2">
            Atualizando seus dados...
          </p>
        </div>
      ) : loading ? (
        <div className="py-4 px-3 text-center">
          <Spinner
            title="Carregando informações de pagamento..."
            minHeight="220px"
          />
        </div>
      ) : (
        <>
          {/* ── Card do Plano e Tempo de Assinatura (sem valor destacado no cabeçalho) ── */}
          <div
            className="modal-pending-plan-card mb-4"
            style={{
              backgroundColor: "#f4eee5",
              borderRadius: "16px",
              padding: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              border: "1px solid #dcd1c2",
              textAlign: "left",
            }}
          >
            <div
              style={{
                width: "70px",
                height: "70px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL || ""}/assets/images/${planImage}`}
                alt={planTitle}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/assets/images/kitNewEssencial.png`;
                }}
              />
            </div>
            <div className="d-flex flex-column text-start" style={{ minWidth: 0, flex: 1 }}>
              <span
                style={{
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: "#2d7a3d",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  lineHeight: 1.25,
                }}
              >
                {planTitle}
              </span>
              <span
                style={{
                  fontSize: "0.92rem",
                  fontWeight: 600,
                  color: "#5c4f3d",
                  marginTop: "4px",
                }}
              >
                {subscriptionDuration}
              </span>
            </div>
          </div>

          <hr className="modal-emag-divider" />

          {/* ── Radios de método (quando há Cartão e PIX disponíveis) ── */}
          {canPayWithCard && hasPix && (
            <div className="modal-emag-method-radios">
              <FormRadio
                label="Cartão de crédito"
                name="pending-payment-method"
                value="credit_card"
                checked={selectedMethod === "credit_card"}
                onChange={() => {
                  setSelectedMethod("credit_card");
                  setPaymentError(null);
                }}
              />
              <FormRadio
                label="PIX"
                name="pending-payment-method"
                value="pix"
                checked={selectedMethod === "pix"}
                onChange={() => {
                  setSelectedMethod("pix");
                  setPaymentError(null);
                }}
              />
            </div>
          )}

          {/* ── Alerta de erro inline (mesmo padrão do ModalAddonPayment) ── */}
          {paymentError && (
            <div
              ref={paymentErrorRef}
              className="alert alert-danger mx-4 mt-3 mb-1 text-start"
              role="alert"
              data-testid="pending-payment-error"
              style={{
                borderRadius: "8px",
                border: "1px solid #f5c2c7",
                backgroundColor: "#f8d7da",
                color: "#842029",
                padding: "12px 14px",
              }}
            >
              {paymentError.title && (
                <strong
                  className="d-block mb-1"
                  style={{
                    fontSize: "0.95rem",
                    fontWeight: "700",
                  }}
                >
                  {paymentError.title}
                </strong>
              )}
              {paymentError.message && (
                <p className="mb-0" style={{ fontSize: "0.88rem" }}>
                  {paymentError.message}
                </p>
              )}
            </div>
          )}

          {/* ── Fluxo Cartão de Crédito (idêntico ao ModalAddonPayment) ── */}
          {canPayWithCard && selectedMethod === "credit_card" ? (
            <>
              <div>
                {/* Abas Cartão salvo / Novo cartão */}
                {savedCards.length > 0 && (
                  <div className="modal-emag-card-tabs">
                    <button
                      type="button"
                      className={`modal-emag-tab${cardTab === "saved" ? " active" : ""}`}
                      disabled={payingCard}
                      onClick={() => {
                        setCardTab("saved");
                        setPaymentError(null);
                      }}
                    >
                      Cartão salvo
                    </button>
                    <button
                      type="button"
                      className={`modal-emag-tab${cardTab === "new" ? " active" : ""}`}
                      disabled={payingCard}
                      onClick={() => {
                        setCardTab("new");
                        setPaymentError(null);
                      }}
                    >
                      Novo cartão
                    </button>
                  </div>
                )}

                {/* Lista de cartões salvos (nome em cima, número mascarado embaixo) */}
                {cardTab === "saved" && savedCards.length > 0 ? (
                  <div className="modal-emag-saved-cards">
                    {savedCards.map((c) => (
                      <label
                        key={c.payment_method_id}
                        className={`modal-emag-card-option${
                          selectedCardId === c.payment_method_id ? " selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="pending-saved-card"
                          value={c.payment_method_id}
                          checked={selectedCardId === c.payment_method_id}
                          disabled={payingCard}
                          onChange={() => {
                            setSelectedCardId(c.payment_method_id);
                            setPaymentError(null);
                          }}
                        />
                        <div className="d-flex flex-column text-start">
                          <strong>{c.description ?? "Sem nome"}</strong>
                          {c.display_number && (
                            <small style={{ color: "#7a6a4f" }}>
                              {c.display_number}
                            </small>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  /* Formulário de novo cartão (mesmos componentes FormInput/FormCheck de ModalAddonPayment) */
                  <form
                    id="pending-invoice-card-form"
                    className="modal-emag-card-form"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <div className="mb-2">
                      <FormInput
                        label="Nome no cartão"
                        name="card_name"
                        value={cardData.card_name}
                        onChange={handleCardChange}
                        dataIugu="full_name"
                        float
                      />
                    </div>
                    <div className="mb-2">
                      <FormInput
                        label="Número do cartão"
                        name="card_number"
                        mask="credit_card_dynamic"
                        value={cardData.card_number}
                        onChange={handleCardChange}
                        onCardTypeChange={({ type, digits }) => {
                          setCardType(type);
                          setCardDigits(digits);
                        }}
                        dataIugu="number"
                        float
                      />
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-7">
                        <FormInput
                          label="Validade (MM/AA)"
                          name="card_expiration"
                          mask="expiration"
                          value={cardData.card_expiration}
                          onChange={handleCardChange}
                          dataIugu="expiration"
                          float
                        />
                      </div>
                      <div className="col-5">
                        <FormInput
                          label="CVV"
                          name="card_cvv"
                          mask="cvv_dynamic"
                          value={cardData.card_cvv}
                          onChange={handleCardChange}
                          cardType={cardType}
                          cardDigits={cardDigits}
                          dataIugu="verification_value"
                          float
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <FormCheck
                        name="pending-save-card"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        label="Salvar cartão para próximas compras"
                      />
                    </div>
                    {saveCard && (
                      <div className="mb-2">
                        <FormInput
                          label="Nome para identificar o cartão"
                          name="card_label_field"
                          value={cardLabel}
                          onChange={(e) => setCardLabel(e.target.value)}
                          float
                        />
                      </div>
                    )}
                  </form>
                )}

                {/* Seletor de Parcelas */}
                <div className="mt-3">
                  <FormSelect
                    label="Parcelas"
                    name="pending-installments"
                    value={String(installments)}
                    onChange={(e) => setInstallments(Number(e.target.value))}
                    list={getInstallmentOptions()}
                  />
                </div>
              </div>

              {/* Footer com aviso de cobrança, aviso da operadora e botão padrão */}
              <div className="modal-emag-footer mt-4">
                {chargeNotice && (
                  <p
                    className="modal-pending-charge-notice text-center mb-3"
                    style={{
                      color: "#3D2C06",
                      fontSize: "0.98rem",
                      fontWeight: 600,
                      lineHeight: 1.45,
                    }}
                  >
                    {chargeNotice}
                  </p>
                )}
                <CardPaymentProcessingNotice
                  active={payingCard}
                  className="mb-3"
                />
                <Button
                  classes="btn-account w-100"
                  label={payingCard ? "AGUARDE..." : "EFETUAR PAGAMENTO"}
                  disabled={payingCard}
                  onClick={handlePayWithCard}
                />
              </div>
            </>
          ) : hasPix && selectedMethod === "pix" ? (
            <div className="modal-emag-pix-qr text-center">
              {canPayWithCard && (
                <h6 className="payment-title mb-2">Pagamento via PIX</h6>
              )}

              {chargeNotice && (
                <p
                  className="modal-pending-charge-notice text-center mb-3"
                  style={{
                    color: "#3D2C06",
                    fontSize: "0.98rem",
                    fontWeight: 600,
                    lineHeight: 1.45,
                  }}
                >
                  {chargeNotice}
                </p>
              )}

              {pixData?.qrcode && (
                <div className="pix-qr-container">
                  <img
                    src={getPixQrSrc(pixData.qrcode)}
                    alt="QR Code PIX"
                    className="pix-qr-img"
                  />
                </div>
              )}

              <div className="pix-timer">
                <small className="d-block mb-1">Tempo para pagamento:</small>
                <CountdownTimer targetDate={targetDate} />
              </div>

              <p className="pix-instructions">
                Aponte a câmera do seu celular para o QR Code acima ou copie o código PIX abaixo:
              </p>

              {pixData?.qrcode_text && (
                <button
                  type="button"
                  className={`btn w-100 mb-3 ${copiedPix ? "btn-success" : "btn-outline-success"}`}
                  style={{ fontWeight: "600", padding: "10px" }}
                  onClick={() => handleCopyText(pixData.qrcode_text, "pix")}
                >
                  {copiedPix ? "✓ Código PIX copiado!" : "Copiar código PIX"}
                </button>
              )}

              <div className="waiting-status d-flex align-items-center justify-content-center gap-2">
                <div className="spinner-border spinner-border-sm" role="status" />
                <span>Aguardando confirmação do pagamento...</span>
              </div>

              {effectiveInvoiceUrl && (
                <div className="mt-4 pt-3 border-top">
                  <a
                    href={effectiveInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small text-muted text-decoration-underline"
                  >
                    Abrir fatura completa no navegador
                  </a>
                </div>
              )}
            </div>
          ) : hasBankSlip ? (
            <div className="p-4 text-center">
              {formattedTotal && (
                <p
                  className="modal-pending-charge-notice text-center mb-3"
                  style={{
                    color: "#3D2C06",
                    fontSize: "0.98rem",
                    fontWeight: 600,
                    lineHeight: 1.45,
                  }}
                >
                  Valor do boleto: <strong>{formattedTotal}</strong>
                </p>
              )}
              {bankSlipData?.digitable_line && (
                <div className="mb-3 text-start">
                  <small className="text-muted d-block mb-1">Linha digitável:</small>
                  <div className="p-2 bg-white border rounded small font-monospace text-break">
                    {bankSlipData.digitable_line}
                  </div>
                  <button
                    type="button"
                    className={`btn btn-sm w-100 mt-2 ${copiedSlip ? "btn-success" : "btn-outline-secondary"}`}
                    onClick={() => handleCopyText(bankSlipData.digitable_line, "slip")}
                  >
                    {copiedSlip ? "✓ Linha copiada!" : "Copiar linha digitável"}
                  </button>
                </div>
              )}

              {bankSlipData?.url && (
                <a
                  href={bankSlipData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-success w-100 mb-3"
                >
                  Visualizar Boleto (PDF)
                </a>
              )}

              <div className="waiting-status d-flex align-items-center justify-content-center gap-2">
                <div className="spinner-border spinner-border-sm" role="status" />
                <span>Aguardando compensação do boleto...</span>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center">
              {error && <p className="text-danger small mb-3">{error}</p>}

              <p className="text-muted mb-4">
                Abra a fatura para concluir o pagamento de forma segura.
              </p>

              {effectiveInvoiceUrl ? (
                <a
                  href={effectiveInvoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success w-100 mb-3"
                  style={{ padding: "10px", fontWeight: "600" }}
                >
                  Abrir fatura para pagamento
                </a>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary w-100"
                  onClick={closeModal}
                >
                  Fechar
                </button>
              )}

              <div className="waiting-status d-flex align-items-center justify-content-center gap-2 mt-3">
                <div className="spinner-border spinner-border-sm" role="status" />
                <span>Aguardando confirmação do pagamento...</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ModalPendingPayment;
